import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Rating,
    RadioGroup,
    Radio,
    FormControlLabel,
    TextField,
    Button,
    CircularProgress,
    Divider,
    IconButton
} from "@mui/material";
import httpcommon from "../../helper/httpcommon";
import { useToast } from "../../hook/useToast";
import LoggedInLayout from "../../layout/LoggedInLayout";
import { ArrowBack } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const FID = "portal_experience";
const styles = {
    header: {
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'rgba(243, 243, 245, 1)',
        height: '46px',
        borderRadius: '6px',
        paddingLeft: '12px',
        marginLeft: '5px',
        marginBottom: '24px',
        width: '95%',
    },
    formContainer: {
        width: '70%',
        marginLeft: '60px',
        marginTop: '30px',
    },

    backBtn: {
        padding: 0,
        marginRight: '8px',
        width: "28px",
        height: "28px",
        backgroundColor: 'rgba(255, 255, 255, 1)',
        borderRadius: '50%',
        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
    },

    backIcon: {
        fontSize: '22px',
        color: 'rgba(157, 158, 158, 1)',
    },

    pageHeading: {
        fontSize: '15px',
        fontWeight: 500,
        color: '#231F20',
    },

};
const PortalFeedbackForm = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [questions, setQuestions] = useState([]);
    const [answers, setAnswers] = useState({});
    const [noData, setNoData] = useState(false);


    /* ---------------- FETCH QUESTIONS ---------------- */
    const getFeedbackQuestions = async () => {
        try {
            setLoading(true);

            const response = await httpcommon.get(
                `/school/customer_feedback_questions/${FID}`
            );

            if (response.status === 200 && Array.isArray(response.data) && response.data.length > 0) {
                setQuestions(response.data);
            } else {
                setQuestions([]);
                setNoData(true);
            }
        } catch (error) {
            setQuestions([]);
            setNoData(true);
            showToast(
                error.response?.data?.error || "Error fetching feedback questions",
                "error"
            );
            setQuestions([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getFeedbackQuestions();
    }, []);

    /* ---------------- HANDLE ANSWER ---------------- */
    const handleChange = (qid, value) => {
        setAnswers(prev => ({
            ...prev,
            [qid]: value
        }));
    };

    /* ---------------- SUBMIT ---------------- */
    const submitFeedback = async () => {
        try {
            setSubmitting(true);

            const payload = {
                fid: FID,
                responses: Object.entries(answers).map(([qid, answer]) => ({
                    qid,
                    answer
                }))
            };

            const response = await httpcommon.post(
                "/school/submit_customer_feedback/",
                payload
            );

            if (response.status === 201 || response.status === 200) {
                setAnswers({});
      showToast(
            "Thank you for your feedback! We appreciate your time and input.",
            );
            }
        } catch (error) {
            showToast(
                error.response?.data?.error || "Error submitting feedback",
                "error"
            );
        } finally {
            setSubmitting(false);
        }
    };

    /* ---------------- QUESTION RENDERER ---------------- */
    const renderInput = (q) => {
        switch (q.type) {
            case "rating":
                return (
                    <>
                        <Rating
                            size="large"
                            sx={{
                                "& .MuiRating-iconEmpty": {
                                    color: "#FCC125"
                                },
                                fontSize: "32px"
                            }}
                            value={answers[q.qid] || 0}
                            onChange={(e, value) => handleChange(q.qid, value)}
                        />
                        <Typography fontSize={14} color="gray" mt={0.5}>
                            (1 = Poor, 5 = Excellent)
                        </Typography>
                    </>

                );

            case "single_choice":
                return (
                    <RadioGroup
                        row
                        value={answers[q.qid] || ""}
                        onChange={(e) => handleChange(q.qid, e.target.value)}
                    >
                        {q.options.map(opt => (
                            <FormControlLabel
                                key={opt}
                                value={opt}
                                control={<Radio />}
                                label={opt}
                            />
                        ))}
                    </RadioGroup>
                );

            case "text":
                return (
                    <TextField
                        fullWidth
                        multiline
                        minRows={1}
                        value={answers[q.qid] || ""}
                        onChange={(e) => handleChange(q.qid, e.target.value)}
                        variant="standard"
                        sx={{
                            "& .MuiInputBase-root:before": {
                                borderBottom: "1px solid #ccc"
                            },
                            "& .MuiInputBase-root:hover:not(.Mui-disabled):before": {
                                borderBottom: "1px solid #000"
                            },
                            "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                    borderTop: "none",
                                    borderLeft: "none",
                                    borderRight: "none",
                                },
                                "&:hover fieldset": {
                                    borderTop: "none",
                                    borderLeft: "none",
                                    borderRight: "none"
                                },
                                "&.Mui-focused fieldset": {
                                    borderTop: "none",
                                    borderLeft: "none",
                                    borderRight: "none"
                                }
                            }
                        }}
                    />
                );

            default:
                return null;
        }
    };


    /* ---------------- UI ---------------- */
    return (
        <LoggedInLayout>
            <Box className="page-container">
                {/* Header */}
                <Box sx={styles.header}>
                    <IconButton onClick={() => {
                        navigate(-1);
                    }} sx={styles.backBtn}>
                        <ArrowBack sx={styles.backIcon} />
                    </IconButton>

                    <Typography sx={styles.pageHeading}>
                        Your Feedback Is Important to Us                    </Typography>
                </Box>
                <Box sx={styles.formContainer} >
                    {!loading && noData && (
                        <Box
                            mt={6}
                            textAlign="center"
                            sx={{
                                color: "#6B7280",
                                backgroundColor: "#F9FAFB",
                                border: "1px dashed #D1D5DB",
                                borderRadius: "8px",
                                padding: "24px"
                            }}
                        >
                            <Typography fontSize={14} fontWeight={500}>
                                Feedback form is currently unavailable
                            </Typography>
                            <Typography fontSize={13} mt={1}>
                                Please try again later.
                            </Typography>
                        </Box>
                    )}
                    {loading ? (
                        <CircularProgress />
                    ) : (
                        questions.map((q, index) => (
                            <Box key={q.qid} mb={4}>
                                <Typography fontSize={14} fontWeight={500} mb={1}>
                                    {index + 1}. {q.label}
                                </Typography>

                                {renderInput(q)}
                            </Box>
                        ))
                    )}


                    {!loading && !noData && <Button
                        variant="contained"
                        onClick={submitFeedback}
                        disabled={submitting}
                        sx={{ backgroundColor: "rgba(247, 170, 71, 1)", color: "#FFF" }}
                    >
                        {submitting ? "Submitting..." : "Submit Feedback"}
                    </Button>}
                </Box>

            </Box>
        </LoggedInLayout>
    );
};

export default PortalFeedbackForm;
